import { FormColumns, FormLabel } from "components/form";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "ui/components/form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/components/dialog";
import { Button } from "ui/components/button";
import ButtonWithLoading from "@/components/button-with-loading";
import { DatePicker } from "components/date-picker-2";
import EmployeeInput from "components/employee/input";
import HeaderPortal from "components/portal/header-portal";
import { Form } from "react-crud-form";
import { Input } from "ui/components/input";
import { PermissionControlledInput } from "permissions/components/input";
import { PermissionGate } from "permissions/components/gate";
import React, { useState } from "react";
import { StatusTabInput } from "components/status/tabs/input";
import { Textarea } from "ui/components/textarea";
import { useHotkeys } from "react-hotkeys-hook";
import { useMilestoneForm } from "./useMilestoneForm";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { FormActions } from "components/form/actions";
import { ROUTE } from "@/lib/utils";

const MilestoneForm = ({ defaultJobId, type }) => {
  const { milestoneId } = useParams();
  const navigate = useNavigate();

  const {
    form,
    requiredFields,
    handleCreate,
    handleRead,
    handleUpdate,
    handleClose,
    apiStatus,
  } = useMilestoneForm({ defaultJobId, type });

  const status = form.watch("status");
  const isCompleted = apiStatus === "completed";
  const isClosed = apiStatus === "closed" || apiStatus === "cancelled";
  const isLocked = isCompleted || isClosed;

  const [confirmation, setConfirmation] = useState({
    open: false,
    title: "",
    description: "",
    action: null,
  });
  const [actionLoading, setActionLoading] = useState(false);

  const handleConfirm = async () => {
    if (!confirmation.action) return;
    setActionLoading(true);
    try {
      await confirmation.action();
      setConfirmation({ ...confirmation, open: false });
      navigate(-1);
    } catch (error) {
    } finally {
      setActionLoading(false);
    }
  };

  // Permission rule constants
  const CREATE_RULE = "create:PC,JC,SC,AM";
  const UPDATE_RULE = "update:PC,JC,MC,SC,AM";

  return (
    <Form
      form={form}
      create={handleCreate}
      read={handleRead}
      update={handleUpdate}
      itemId={milestoneId || "+"}
    >
      {({
        isCreating,
        createItem,
        isNewItem,
        isUpdating,
        updateItem,
        isDirty,
      }) => {
        // Permission rules based on item state
        const formRule = isNewItem ? CREATE_RULE : UPDATE_RULE;
        const coordinatorRule = isNewItem ? CREATE_RULE : "update:PC,SC,JC,AM";
        useHotkeys(
          "ctrl+s, cmd+s",
          (event) => {
            event.preventDefault();
            createItem();
          },
          { preventDefault: true },
        );

        return (
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={formRule}
                    collection="milestones"
                    field={field.name}
                    type="form"
                    isDisabled={isLocked}
                  >
                    <FormLabel>Status</FormLabel>
                    <StatusTabInput
                      {...field}
                      collection="milestones"
                      remove={
                        type === "bucket" || type === "recurring"
                          ? ["cancelled", "completed"]
                          : ["cancelled", "closed"]
                      }
                      removeArchived={true}
                    />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={formRule}
                    collection="milestones"
                    field={field.name}
                    type="form"
                    isDisabled={isLocked}
                  >
                    <FormLabel isRequired={requiredFields.name}>
                      Title
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Enter milestone title" {...field} />
                    </FormControl>
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            {(type === "hourly" || type === "bucket") &&
              (status === "closed" || status === "completed") && (
                <FormField
                  control={form.control}
                  name="billable_hours"
                  render={({ field }) => (
                    <FormItem>
                      <PermissionControlledInput
                        rule={formRule}
                        collection="milestones"
                        field={field.name}
                        type="form"
                        isDisabled={isLocked}
                      >
                        <FormLabel isRequired={requiredFields.billable_hours}>
                          Billable Hours
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="Enter billable hours"
                            {...field}
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              field.onChange(
                                val === "" ? undefined : Number(val),
                              );
                            }}
                          />
                        </FormControl>
                      </PermissionControlledInput>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

            <FormField
              control={form.control}
              name="coordinator"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={coordinatorRule}
                    collection="milestones"
                    field={field.name}
                    type="form"
                    isDisabled={isLocked}
                  >
                    <FormLabel isRequired={requiredFields.coordinator}>
                      Milestone Coordinator
                    </FormLabel>
                    <FormControl>
                      <EmployeeInput
                        type="coordinator"
                        placeholder="Select milestone coordinator"
                        targetField="coordinator"
                        {...field}
                        filter={{
                          _and: [
                            {
                              role: {
                                name: {
                                  _in: ["PC", "JC", "MC", "SC", "AM"],
                                },
                              },
                            },
                          ],
                        }}
                      />
                    </FormControl>
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormColumns>
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={formRule}
                      collection="milestones"
                      field={field.name}
                      type="form"
                      isDisabled={isLocked}
                    >
                      <FormLabel isRequired={requiredFields.start_date}>
                        Start Date
                      </FormLabel>
                      <FormControl>
                        <DatePicker {...field} />
                      </FormControl>
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={formRule}
                      collection="milestones"
                      field={field.name}
                      type="form"
                      isDisabled={isLocked}
                    >
                      <FormLabel isRequired={requiredFields.end_date}>
                        End Date
                      </FormLabel>
                      <FormControl>
                        <DatePicker {...field} />
                      </FormControl>
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormColumns>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={formRule}
                    collection="milestones"
                    field={field.name}
                    type="form"
                    isDisabled={isLocked}
                  >
                    <FormLabel isRequired={requiredFields.description}>
                      Description
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Enter description"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <HeaderPortal>
              <FormActions
                isNewItem={isNewItem}
                isUpdating={isUpdating}
                isCreating={isCreating}
                isDirty={isDirty}
                updateItem={() => updateItem().then(() => navigate(-1))}
                createItem={() =>
                  createItem().then((response) =>
                    navigate(ROUTE.MILESTONE(response?.id)),
                  )
                }
                isLocked={isLocked}
                cancelModalTitle="Cancel Milestone"
                cancelModalDescription="Are you sure you want to cancel this milestone? This action cannot be undone."
                onClose={() =>
                  setConfirmation({
                    open: true,
                    title: "Cancel Milestone",
                    description:
                      "Are you sure you want to cancel this milestone? This action cannot be undone.",
                    action: handleClose,
                  })
                }
              />
            </HeaderPortal>

            <Dialog
              open={confirmation.open}
              onOpenChange={(open) =>
                setConfirmation({ ...confirmation, open })
              }
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{confirmation.title}</DialogTitle>
                  <DialogDescription>
                    {confirmation.description}
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() =>
                      setConfirmation({ ...confirmation, open: false })
                    }
                  >
                    Cancel
                  </Button>
                  <ButtonWithLoading
                    variant="destructive"
                    onClick={handleConfirm}
                    loading={actionLoading}
                  >
                    Confirm
                  </ButtonWithLoading>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        );
      }}
    </Form>
  );
};

export default MilestoneForm;
